import superAdminApiClient from './superAdminApi.service';
import {
  ApplyStorePresetBody,
  CreateSuperAdminStoreData,
  PlatformJob,
  PlatformJobSchedule,
  PlatformStoreAccessSession,
  PlatformStoreDetailPayload,
  PlatformStoresListPayload,
  PlatformStoresQuery,
  PlatformTreatmentType,
  SuperAdminDashboardSummary,
  SuperAdminInviteData,
  SuperAdminInvitation,
  SuperAdminPresetGuards,
  SuperAdminStore,
  SuperAdminStorePreset,
  SuperAdminUser,
  UpdateStoreTemplateFlagsData,
} from '../types/superAdmin.types';
import { AiQuota, AiQuotaTier } from '../types/ai.types';

export interface SuperAdminStoresQuery {
  search?: string;
  created_by_super_admin_id?: string;
  is_template?: '' | 'true' | 'false';
}

class SuperAdminService {
  async getDashboardSummary(): Promise<SuperAdminDashboardSummary> {
    const response = await superAdminApiClient.get('/super-admin/dashboard');
    return response.data.data.summary;
  }

  async getSuperAdminAccounts(): Promise<Pick<SuperAdminUser, 'id' | 'first_name' | 'last_name' | 'email'>[]> {
    const response = await superAdminApiClient.get('/super-admin/accounts');
    return response.data.data.super_admins;
  }

  async getInvitations(): Promise<SuperAdminInvitation[]> {
    const response = await superAdminApiClient.get('/super-admin/invitations');
    return response.data.data.invitations;
  }

  async createInvitation(data: SuperAdminInviteData): Promise<{ invitation: SuperAdminInvitation; invitation_link: string }> {
    const response = await superAdminApiClient.post('/super-admin/invitations', data);
    return response.data.data;
  }

  async getSuperAdminStores(
    params?: SuperAdminStoresQuery,
    options?: { signal?: AbortSignal }
  ): Promise<SuperAdminStore[]> {
    const response = await superAdminApiClient.get('/super-admin/stores', {
      params,
      signal: options?.signal,
    });
    return response.data.data.stores;
  }

  async createSuperAdminStore(data: CreateSuperAdminStoreData): Promise<SuperAdminStore> {
    const response = await superAdminApiClient.post('/super-admin/stores', data);
    return response.data.data.store;
  }

  async updateStoreTemplate(storeId: number, body: UpdateStoreTemplateFlagsData): Promise<SuperAdminStore> {
    const response = await superAdminApiClient.patch(`/super-admin/stores/${storeId}/template`, body);
    return response.data.data.store;
  }

  async getPresets(): Promise<SuperAdminStorePreset[]> {
    const response = await superAdminApiClient.get('/super-admin/presets');
    return response.data.data.presets;
  }

  async getStorePresetGuards(storeId: number): Promise<SuperAdminPresetGuards> {
    const response = await superAdminApiClient.get(`/super-admin/stores/${storeId}/preset-guards`);
    return response.data.data;
  }

  async applyStorePreset(storeId: number, body: ApplyStorePresetBody): Promise<{ result: Record<string, unknown> }> {
    const response = await superAdminApiClient.post(`/super-admin/stores/${storeId}/apply-preset`, body);
    return response.data.data as { result: Record<string, unknown> };
  }

  async getPlatformStores(
    params?: PlatformStoresQuery,
    options?: { signal?: AbortSignal }
  ): Promise<PlatformStoresListPayload> {
    const response = await superAdminApiClient.get('/super-admin/platform-stores', {
      params,
      signal: options?.signal,
    });
    return response.data.data;
  }

  async reindexPlatformStoreSolr(storeId: number): Promise<{
    indexed: number;
    failed: number;
    last_solr_reindex_at: string | null;
  }> {
    const response = await superAdminApiClient.post(
      `/super-admin/platform-stores/${storeId}/reindex-solr`,
      {},
      { timeout: 600000 }
    );
    return response.data.data;
  }

  async getPlatformStore(storeId: number): Promise<PlatformStoreDetailPayload> {
    const response = await superAdminApiClient.get(`/super-admin/platform-stores/${storeId}`);
    return response.data.data;
  }

  async createPlatformStoreAccessSession(
    storeId: number,
    simulateUserId?: number
  ): Promise<PlatformStoreAccessSession> {
    const response = await superAdminApiClient.post(
      `/super-admin/platform-stores/${storeId}/access-session`,
      simulateUserId ? { simulateUserId } : {}
    );
    return response.data.data;
  }

  async endPlatformStoreAccessSession(storeId: number): Promise<{ removed: boolean }> {
    const response = await superAdminApiClient.post(`/super-admin/platform-stores/${storeId}/end-access-session`);
    return response.data.data;
  }

  async getTreatmentTypes(): Promise<PlatformTreatmentType[]> {
    const response = await superAdminApiClient.get('/super-admin/treatments/types');
    return response.data.data.types;
  }

  async listTreatmentJobs(params?: { job_type?: string; page?: number; limit?: number }): Promise<{
    jobs: PlatformJob[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const response = await superAdminApiClient.get('/super-admin/treatments/jobs', { params });
    return response.data.data;
  }

  async runTreatmentJob(jobType: string): Promise<PlatformJob> {
    const response = await superAdminApiClient.post('/super-admin/treatments/jobs', { job_type: jobType });
    return response.data.data.job;
  }

  async listTreatmentSchedules(): Promise<PlatformJobSchedule[]> {
    const response = await superAdminApiClient.get('/super-admin/treatments/schedules');
    return response.data.data.schedules;
  }

  async updateTreatmentSchedule(
    jobType: string,
    body: { is_active?: boolean; schedule_type?: string; schedule_config?: { time?: string; timezone?: string } }
  ): Promise<PlatformJobSchedule> {
    const response = await superAdminApiClient.put(`/super-admin/treatments/schedules/${jobType}`, body);
    return response.data.data.schedule;
  }

  async getStoreAi(storeId: number): Promise<{ quota: AiQuota; tiers: AiQuotaTier[] }> {
    const response = await superAdminApiClient.get(`/super-admin/platform-stores/${storeId}/ai`);
    return response.data.data;
  }

  async setStoreAiTier(storeId: number, tier: string): Promise<{ quota: AiQuota }> {
    const response = await superAdminApiClient.put(`/super-admin/platform-stores/${storeId}/ai/tier`, { tier });
    return response.data.data;
  }
}

export default new SuperAdminService();
